import { get, set, del } from "idb-keyval";
import { GameSettings } from "./controller";
import { Position, Side } from "../engine/position";

const KEY_SETTINGS = "bg.settings";
const KEY_GAME = "bg.game";

const DEFAULT_SETTINGS: GameSettings = {
  matchLength: 1,
  cubeEnabled: false,
  whitePlayer: "human",
  blackPlayer: "cpu",
  whiteName: "You",
  blackName: "Computer",
  cpuDifficulty: "casual",
  tutorMode: "off",
  showPipCount: false,
  showEquity: false,
  soundEnabled: true,
  autoRoll: true,
};

export async function loadSettings(): Promise<GameSettings> {
  try {
    const raw = (await get(KEY_SETTINGS)) as
      | (Partial<GameSettings> & { tutorEnabled?: boolean })
      | undefined;
    const v = { ...(raw ?? {}) };
    // Migrate legacy boolean tutor flag → the three-way mode.
    if (v.tutorMode === undefined && v.tutorEnabled !== undefined) {
      v.tutorMode = v.tutorEnabled ? "tutor" : "off";
    }
    delete v.tutorEnabled;
    return { ...DEFAULT_SETTINGS, ...v };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export async function saveSettings(s: GameSettings): Promise<void> {
  try {
    await set(KEY_SETTINGS, s);
  } catch {
    // ignore
  }
}

// Saved-game snapshot. Games are checkpointed at turn boundaries (after each
// committed play, with dice unrolled), so a resumed game always re-enters at
// "next player to roll" — no mid-turn dice or pending sub-moves to restore.
export interface SavedGame {
  version: 1;
  position: {
    points: number[];
    barUs: number;
    barThem: number;
    offUs: number;
    offThem: number;
    turn: Side;
    cube: { value: number; owner: Side | null };
    score: [number, number];
    matchLength: number;
    crawford: boolean;
  };
  whiteScore: number;
  blackScore: number;
  gameNumber: number;
  crawfordPlayed: boolean;
}

export function serializeGame(state: {
  position: Position;
  whiteScore: number;
  blackScore: number;
  gameNumber: number;
  crawfordPlayed: boolean;
}): SavedGame {
  const p = state.position;
  return {
    version: 1,
    position: {
      points: Array.from(p.points),
      barUs: p.barUs,
      barThem: p.barThem,
      offUs: p.offUs,
      offThem: p.offThem,
      turn: p.turn,
      cube: { ...p.cube },
      score: [p.score[0], p.score[1]],
      matchLength: p.matchLength,
      crawford: p.crawford,
    },
    whiteScore: state.whiteScore,
    blackScore: state.blackScore,
    gameNumber: state.gameNumber,
    crawfordPlayed: state.crawfordPlayed,
  };
}

export function deserializeGame(blob: unknown): { position: Position; saved: SavedGame } | null {
  const b = blob as SavedGame | null | undefined;
  if (!b || b.version !== 1 || !b.position || !Array.isArray(b.position.points)) return null;
  if (b.position.points.length !== 24) return null;
  // Strict field validation. The blob crosses a trust boundary (IndexedDB is
  // writable by anything on the origin), and downstream code interpolates
  // scores into HTML — nothing non-numeric may pass.
  const int = (v: unknown): v is number => Number.isInteger(v);
  const p = b.position;
  if (!p.points.every((v) => int(v) && v >= -15 && v <= 15)) return null;
  if (!int(p.barUs) || !int(p.barThem) || !int(p.offUs) || !int(p.offThem)) return null;
  if (p.turn !== 0 && p.turn !== 1) return null;
  if (!int(b.whiteScore) || !int(b.blackScore) || !int(b.gameNumber)) return null;
  if (typeof b.crawfordPlayed !== "boolean") return null;
  const cubeValue = p.cube?.value ?? 1;
  const cubeOwner = p.cube?.owner ?? null;
  if (!int(cubeValue) || (cubeOwner !== null && cubeOwner !== 0 && cubeOwner !== 1)) return null;
  const score0 = p.score?.[0] ?? 0;
  const score1 = p.score?.[1] ?? 0;
  const matchLength = p.matchLength ?? 1;
  if (!int(score0) || !int(score1) || !int(matchLength)) return null;
  const position: Position = {
    points: Int8Array.from(p.points),
    barUs: p.barUs,
    barThem: p.barThem,
    offUs: p.offUs,
    offThem: p.offThem,
    turn: p.turn,
    dice: null,
    cube: { value: cubeValue, owner: cubeOwner },
    score: [score0, score1],
    matchLength,
    crawford: p.crawford === true,
  };
  return { position, saved: b };
}

export async function saveGame(blob: SavedGame | null): Promise<void> {
  try {
    await set(KEY_GAME, blob);
  } catch {
    // ignore
  }
}

export async function loadGame(): Promise<unknown | null> {
  try {
    return (await get(KEY_GAME)) ?? null;
  } catch {
    return null;
  }
}

export async function clearGame(): Promise<void> {
  try {
    await del(KEY_GAME);
  } catch {
    // ignore
  }
}
